import React, { useState } from 'react';

/**
 * Link-based authentication flows that live outside the logged-in app:
 *   /accept-invite?token=…  — set a password to activate an invited account
 *   /forgot-password        — request a password-reset email
 *   /reset-password?token=… — choose a new password from a reset link
 *
 * Each is reached directly by URL (from an email), so this renders before the
 * auth gate in App.js. On success the account's session is stored and the app
 * is reloaded at '/', landing the user logged in.
 */

const B = {
  bg:          '#faf7f2',
  surface:     '#ffffff',
  border:      'rgba(60,45,30,0.10)',
  borderStrong:'rgba(60,45,30,0.16)',
  text:        '#2a241d',
  muted:       '#6b6055',
  faint:       '#9a8f82',
  green:       '#7BC19E',
  greenDark:   '#5fa882',
  charcoal:    '#3A3D3C',
};
const font = "'Nunito', -apple-system, sans-serif";

const inputStyle = {
  width: '100%', padding: '0.6rem 0.85rem',
  border: `1.5px solid ${B.borderStrong}`,
  borderRadius: 8, fontSize: '0.95rem',
  color: B.text, background: B.bg,
  outline: 'none', fontFamily: font,
  transition: 'border-color 0.15s, box-shadow 0.15s',
  boxSizing: 'border-box',
};

const API_URL = process.env.REACT_APP_API_URL || '';

function storeSessionAndEnter(data) {
  localStorage.setItem('auth_token',    data.token || '');
  localStorage.setItem('student_id',    data.user_id || data.student_id || '');
  localStorage.setItem('account_type',  data.account_type || '');
  localStorage.setItem('display_name',  data.display_name || '');
  localStorage.setItem('active_avatar', data.active_avatar || 'avatar_default');
  localStorage.setItem('active_ring',   data.active_ring   || 'ring_none');
  localStorage.setItem('active_pet',    data.active_pet    || 'pet_none');
  if (data.theme_a_access === false) localStorage.setItem('theme_a_access', '0');
  else localStorage.removeItem('theme_a_access');
  window.location.assign('/');
}

function Shell({ title, children }) {
  return (
    <div style={{ fontFamily: font, background: B.bg, minHeight: '100vh', WebkitFontSmoothing: 'antialiased' }}>
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, height: 62,
        background: B.surface, borderBottom: `1px solid ${B.border}`,
        display: 'flex', alignItems: 'center', padding: '0 1.5rem', zIndex: 100,
      }}>
        <a href="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
          <img src="/logo.svg" alt="brookebit" height={46} style={{ display: 'block' }} />
        </a>
      </nav>
      <main style={{
        paddingTop: 62, minHeight: '100vh',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '62px 1.5rem 4rem',
      }}>
        <div style={{
          background: B.surface, borderRadius: 16,
          boxShadow: '0 1px 0 rgba(255,255,255,0.6) inset, 0 8px 24px rgba(50,30,15,0.07)',
          border: `1px solid ${B.border}`, padding: '2rem 1.75rem 1.75rem',
          width: 320, maxWidth: '100%',
        }}>
          <h2 style={{ fontSize: '1.2rem', fontWeight: 800, color: B.charcoal, margin: '0 0 1.25rem' }}>{title}</h2>
          {children}
        </div>
      </main>
    </div>
  );
}

const focusOn  = e => { e.target.style.borderColor = B.green; e.target.style.boxShadow = '0 0 0 3px rgba(123,193,158,0.18)'; };
const focusOff = e => { e.target.style.borderColor = B.borderStrong; e.target.style.boxShadow = 'none'; };

function ErrorBox({ children }) {
  if (!children) return null;
  return (
    <div style={{
      padding: '10px 14px', background: '#fdf2f2', color: '#c0392b',
      border: '1px solid #fbe2e2', borderRadius: 8, fontSize: '0.85rem', marginBottom: '1rem',
    }}>{children}</div>
  );
}

function PasswordFields({ password, setPassword, confirm, setConfirm, autoComplete }) {
  return (
    <>
      <div style={{ marginBottom: '1rem' }}>
        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: B.text, marginBottom: '0.35rem' }}>New password</label>
        <input type="password" required value={password} onChange={e => setPassword(e.target.value)}
          placeholder="At least 8 characters" autoComplete={autoComplete}
          style={inputStyle} onFocus={focusOn} onBlur={focusOff} />
      </div>
      <div style={{ marginBottom: 0 }}>
        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: B.text, marginBottom: '0.35rem' }}>Confirm password</label>
        <input type="password" required value={confirm} onChange={e => setConfirm(e.target.value)}
          placeholder="Re-enter your password" autoComplete={autoComplete}
          style={inputStyle} onFocus={focusOn} onBlur={focusOff} />
      </div>
    </>
  );
}

function SubmitButton({ loading, children }) {
  return (
    <button type="submit" disabled={loading} style={{
      width: '100%', padding: '0.72rem', background: B.charcoal, color: '#fff',
      border: 'none', borderRadius: 8, fontSize: '0.9rem', fontWeight: 800,
      cursor: loading ? 'not-allowed' : 'pointer', marginTop: '1.25rem',
      letterSpacing: '0.5px', fontFamily: font, opacity: loading ? 0.7 : 1,
    }}>{children}</button>
  );
}

function bothPasswords(password, confirm) {
  if (password.length < 8) return 'Password must be at least 8 characters.';
  if (password !== confirm) return 'Passwords do not match.';
  return null;
}

// ── Accept invite ────────────────────────────────────────────────────────────

function AcceptInvite({ token }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  if (!token) {
    return <Shell title="Invite link missing">
      <p style={{ color: B.muted, fontSize: '0.9rem', lineHeight: 1.6 }}>
        This page needs an invite link. Please open the link from your invitation email,
        or ask your teacher to resend it.
      </p>
    </Shell>;
  }

  const submit = async (e) => {
    e.preventDefault();
    const v = bothPasswords(password, confirm);
    if (v) { setError(v); return; }
    setError(''); setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/register`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.reason || 'Could not activate your account.');
      storeSessionAndEnter(data);
    } catch (err) { setError(err.message); setLoading(false); }
  };

  return (
    <Shell title={<>Set your brooke<span style={{ color: B.green }}>bit</span> password</>}>
      <p style={{ color: B.muted, fontSize: '0.86rem', lineHeight: 1.55, margin: '0 0 1.25rem' }}>
        Welcome! Choose a password to activate your account and start learning.
      </p>
      <ErrorBox>{error}</ErrorBox>
      <form onSubmit={submit}>
        <PasswordFields password={password} setPassword={setPassword} confirm={confirm} setConfirm={setConfirm} autoComplete="new-password" />
        <SubmitButton loading={loading}>{loading ? 'Activating…' : 'ACTIVATE ACCOUNT'}</SubmitButton>
      </form>
    </Shell>
  );
}

// ── Forgot password ───────────────────────────────────────────────────────────

function ForgotPassword() {
  const [email, setEmail]     = useState('');
  const [sent, setSent]       = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await fetch(`${API_URL}/api/request-password-reset`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
    } catch (_) { /* shown as success regardless — no account enumeration */ }
    setSent(true); setLoading(false);
  };

  if (sent) {
    return <Shell title="Check your email">
      <p style={{ color: B.muted, fontSize: '0.9rem', lineHeight: 1.6 }}>
        If an account exists for <strong>{email}</strong>, we've sent a link to reset
        your password. The link is valid for 1 hour.
      </p>
      <p style={{ marginTop: '1.25rem' }}>
        <a href="/" style={{ color: B.greenDark, fontWeight: 700, fontSize: '0.85rem', textDecoration: 'none' }}>← Back to log in</a>
      </p>
    </Shell>;
  }

  return (
    <Shell title="Reset your password">
      <p style={{ color: B.muted, fontSize: '0.86rem', lineHeight: 1.55, margin: '0 0 1.25rem' }}>
        Enter your email and we'll send you a link to choose a new password.
      </p>
      <form onSubmit={submit}>
        <div style={{ marginBottom: 0 }}>
          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: B.text, marginBottom: '0.35rem' }}>Email</label>
          <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
            placeholder="you@school.com" autoComplete="email"
            style={inputStyle} onFocus={focusOn} onBlur={focusOff} />
        </div>
        <SubmitButton loading={loading}>{loading ? 'Sending…' : 'SEND RESET LINK'}</SubmitButton>
      </form>
      <p style={{ textAlign: 'center', marginTop: '1.25rem' }}>
        <a href="/" style={{ color: B.greenDark, fontWeight: 700, fontSize: '0.83rem', textDecoration: 'none' }}>← Back to log in</a>
      </p>
    </Shell>
  );
}

// ── Reset password ─────────────────────────────────────────────────────────────

function ResetPassword({ token }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  if (!token) {
    return <Shell title="Reset link missing">
      <p style={{ color: B.muted, fontSize: '0.9rem', lineHeight: 1.6 }}>
        This page needs a reset link. Please open the link from your password-reset email,
        or <a href="/forgot-password" style={{ color: B.greenDark, fontWeight: 700, textDecoration: 'none' }}>request a new one</a>.
      </p>
    </Shell>;
  }

  const submit = async (e) => {
    e.preventDefault();
    const v = bothPasswords(password, confirm);
    if (v) { setError(v); return; }
    setError(''); setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/reset-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.reason || 'Could not reset your password.');
      storeSessionAndEnter(data);
    } catch (err) { setError(err.message); setLoading(false); }
  };

  return (
    <Shell title="Choose a new password">
      <ErrorBox>{error}</ErrorBox>
      <form onSubmit={submit}>
        <PasswordFields password={password} setPassword={setPassword} confirm={confirm} setConfirm={setConfirm} autoComplete="new-password" />
        <SubmitButton loading={loading}>{loading ? 'Saving…' : 'SET NEW PASSWORD'}</SubmitButton>
      </form>
    </Shell>
  );
}

// ── Router ─────────────────────────────────────────────────────────────────────

export default function AuthFlows() {
  const path  = window.location.pathname.replace(/\/+$/, '') || '/';
  const token = new URLSearchParams(window.location.search).get('token') || '';

  if (path === '/accept-invite')  return <AcceptInvite token={token} />;
  if (path === '/reset-password') return <ResetPassword token={token} />;
  return <ForgotPassword />;
}

export const AUTH_FLOW_PATHS = ['/accept-invite', '/forgot-password', '/reset-password'];
